/**
 * Print one message (DESIGN.md T10 message actions): header block + sanitized body in a hidden,
 * sandboxed iframe (no scripts; `allow-modals` for the print dialog, `allow-same-origin` so the app
 * can call print() on it). Remote images stay blocked unless the user allowed them for the message.
 */
import { buildMailDocument, escapeHtml, sanitizeMailHtml } from './sanitize';

export interface PrintableMessage {
  subject: string;
  /** Label/value rows above the body (From, To, Cc, Date), already localized. */
  rows: readonly (readonly [string, string])[];
  /** Raw mail HTML (sanitized here). */
  html: string;
  allowRemote?: boolean;
}

/** The printable document (exported for tests). */
export function printDocument({ subject, rows, html, allowRemote = false }: PrintableMessage): string {
  const body = sanitizeMailHtml(html, { allowRemote });
  const head =
    `<header style="margin-bottom:16px;padding-bottom:8px;border-bottom:1px solid currentColor">` +
    `<h1 style="font-size:18px;margin:0 0 8px">${escapeHtml(subject)}</h1>` +
    rows.map(([label, value]) => `<div><b>${escapeHtml(label)}:</b> ${escapeHtml(value)}</div>`).join('') +
    '</header>';
  return buildMailDocument(head + body.html, { allowRemote, ownColors: true });
}

export function printMail(message: PrintableMessage): void {
  printHtml(printDocument(message));
}

/** Prints a complete HTML document (built and escaped by the caller) from a hidden sandboxed frame. */
export function printHtml(html: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-modals allow-same-origin');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.className = 'fixed size-0 border-0 opacity-0';
  const cleanup = () => frame.remove();
  frame.onload = () => {
    const view = frame.contentWindow;
    if (!view) return cleanup();
    view.addEventListener('afterprint', cleanup, { once: true });
    view.focus();
    view.print();
    // Browsers without afterprint in sandboxed frames: remove later.
    window.setTimeout(cleanup, 60_000);
  };
  frame.srcdoc = html;
  document.body.appendChild(frame);
}
